import React, { useMemo } from 'react'
import {
  AttributeGroup,
  cleanGroupAttributes
} from './AttributeGroup'
import { ProductDetail } from './ProductDetail'
import ProductImages from './ProductImages'
import type {
    CatalogProduct,
    ProductAttribute,
    ProductAttributeGroup,
    ProductCategory,
    VariesBy,
} from "../types";

// region TypeScript Interfaces
export interface AttributesAndVariationsProps {
    groupedAttributes: ProductAttributeGroup;
    formDisabled?: boolean;
    catalog?: string;
    productId?: string;
    product: CatalogProduct;
    retailerProduct: CatalogProduct | null;
    availableCategories: ProductCategory[];
    validationErrors: Record<string, string>;
    updateProductState: (attribute: string, value: any) => void;
    onAIEnrich?: (attributeName: string) => void;
    variesBy: VariesBy;
    availableAttributes: ProductAttribute[];
    partnerId?: string | number;
    isRetailer?: boolean;
    refreshing?: boolean;
    aiApplyCounter?: number;
    // The container forwards a few extra props (setAddVariationDialog,
    // setAddVariationSkuDialog, setVariations, hasInventoryManagePermission,
    // updateVariationState, setVariationDeletionDialogOpen,
    // setVariationDeletionIndex, allowVariations, variations,
    // variationValidationErrors) that this component does not consume but the
    // container passes through. Keep the prop bag open.
    [key: string]: any;
}
// endregion

const disallowedVariesAttributes = ['sku']
const disallowedVariesTypes = ['kvplist']

const AttributesAndVariations = ({
  groupedAttributes,
  formDisabled,
  catalog,
  productId,
  product,
  retailerProduct,
  availableCategories,
  validationErrors,
  updateProductState,
  onAIEnrich,
  variesBy,
  availableAttributes,
  partnerId,
  isRetailer,
  refreshing,
  aiApplyCounter
}: AttributesAndVariationsProps) => {
  const filteredVariesByOptions = useMemo(() =>
    variesBy.options
      .filter(
        (option, index, self) =>
          self.findIndex(t => t.label === option.label) === index
      )
      .filter(
        option => !disallowedVariesAttributes.includes(option.value)
      )
      .filter(field => !disallowedVariesTypes.includes(field.type as string)),
    [variesBy.options]
  );

  const filteredVariesByFields = useMemo(() =>
    variesBy.fields
      .filter(
        (field, index, self) =>
          self.findIndex(t => t.label === field.label) === index
      )
      .filter(field => !disallowedVariesAttributes.includes(field.value))
      .filter(
        field =>
          !variesBy.options
            .map(option => option.value)
            .includes(field.value)
      )
      .filter(field => !disallowedVariesTypes.includes(field.type as string)),
    [variesBy.fields, variesBy.options]
  );

  return (
    <>
      {Object.keys(groupedAttributes).map((group, index) => {
        const cleanedGroupAttributes = cleanGroupAttributes(groupedAttributes[group], product);
        if (cleanedGroupAttributes.length === 0) return null

        return (
          <React.Fragment key={index}>
            <AttributeGroup
              disabled={formDisabled}
              currentCatalog={catalog}
              group={cleanedGroupAttributes}
              name={group}
              productId={productId}
              product={product}
              retailerProduct={retailerProduct}
              availableCategories={availableCategories}
              errors={validationErrors}
              onChange={updateProductState}
              onAIEnrich={onAIEnrich}
              refreshing={refreshing}
              aiApplyCounter={aiApplyCounter}
              filteredVariesByOptions={filteredVariesByOptions}
              filteredVariesByFields={filteredVariesByFields}
              availableAttributes={availableAttributes}
              partnerId={partnerId}
              isRetailer={isRetailer}
              catalog={catalog}
              index={index}
            />

            {group === 'Basics' &&
              groupedAttributes['Media'] &&
              groupedAttributes['Media'].some(
                attribute => attribute.Type === 'image'
              ) && (
                <ProductDetail
                  key={'media'}
                  listkey={'media'}
                  title={'Media'}
                  id={'Media'}
                  content={
                    <ProductImages
                      groupedAttributes={groupedAttributes}
                      group={'Media'}
                      catalog={catalog}
                      partnerId={partnerId}
                      isRetailer={isRetailer}
                      product={product}
                      updateProductState={updateProductState}
                      disabled={formDisabled}
                    />
                  }
                />
              )}
          </React.Fragment>
        )
      })}
    </>
  )
}

export default React.memo(AttributesAndVariations);
